import React, { useState } from "react";
import { Edit2, Trash2 } from "lucide-react";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import {
  updateBudget,
  deleteBudget,
  getLastThreeMonthsTransactionData,
  getMonthlyTransactionData,
  getYearlyTransactionData,
  getMonthlyTransactions,
  getYearlyTransactions,
  getLast3MonthsTransactions,
} from "../../../store/engagementMetricsSlice";

const optionsList = [
  { value: "All", label: "All" },
  { value: "Within Budget", label: "Within Budget" },
  { value: "Above Budget", label: "Above Budget" },
];

const getSpendStatus = (txn) => {
  return Number(txn.amount) > Number(txn.budget)
    ? "Above Budget"
    : "Within Budget";
};

const statusColors = {
  "Within Budget": "bg-green-100 text-green-700",
  "Above Budget": "bg-red-100 text-red-700",
};

const customStyles = {
  menu: (provided) => ({
    ...provided,
    textAlign: "start",
    background: "white",
    borderRadius: "0.5rem",
    color: "black",
    border: "1px solid #EDEDED",
  }),
  control: (provided, state) => ({
    ...provided,
    background: "white",
    border: "1px solid #EDEDED",
    textAlign: "center",
    height: "38px",
    borderRadius: "4px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(128, 128, 128, 0.2)" : "none",
    "&:hover": {
      border: "1px solid #EDEDED",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "black",
  }),
  option: (provided, state) => ({
    ...provided,
    background: "white",
    color: state.isFocused ? "#9CA3AF" : "black",
    padding: "6px",
    borderRadius: "0.5rem",
    cursor: "pointer",
  }),

  placeholder: (provided) => ({
    ...provided,
    color: "black",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: "black",
    "&:hover": {
      color: "black",
    },
  }),
};

export default function SpendTable({ filter }) {
  const {
    monthlyTransactions,
    lastThreeMonthsTransactions,
    yearlyTransactions,
  } = useSelector((state) => state.engagementMetrics);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [selectedOption, setSelectedOption] = useState(optionsList[0]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("filter");
  const [editableRowData, setEditableRowData] = useState(null);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.userDetails.userDetails);
  const userId = useSelector((state) => state.auth.id);
  const supplier = useSelector((state) => state.auth.selectedSupplier);
  const getFilteredData = () => {
    switch (filter) {
      case "Monthly":
        return monthlyTransactions;
      case "Last 3 Months":
        return lastThreeMonthsTransactions;
      case "Yearly":
        return yearlyTransactions;
      default:
        return monthlyTransactions;
    }
  };

  const filteredData = getFilteredData();

  const handleSearch = (selected) => {
    const query = searchQuery.toLowerCase();
    const buyerIdToUse = user?.isTestUser
      ? user?.id
      : user?.buyerAccountId || userId;
    const searchString = selected && selected !== "All" ? selected : query;

    const payload = {
      supplierId: supplier._id,
      buyerId: buyerIdToUse,
      searchString: searchString,
    };

    if (filter === "Monthly" || selected === "All") {
      dispatch(getMonthlyTransactions(payload));
    } else if (filter === "Yearly") {
      dispatch(getYearlyTransactions(payload));
    } else if (filter === "Last 3 Months") {
      dispatch(getLast3MonthsTransactions(payload));
    }
    setViewMode("search");
    setSearchQuery("");
  };

  const handleUpdateBudget = async () => {
    const updatedRow = {
      ...editableRowData,
    };

    const budgetId = editableRowData.budgetId;
    const supplierId = supplier._id;
    const buyerId = user?.isTestUser
      ? user?.id
      : user?.buyerAccountId || userId;

    const payload = {
      budgetId,
      supplierId,
      buyerId,
      data: {
        month: updatedRow.month,
        year: new Date().getFullYear(),
        amount: Number(updatedRow.budget),
      },
    };

    try {
      await dispatch(updateBudget(payload)).unwrap();
      const buyerIdToUse = user?.isTestUser
        ? user?.id
        : user?.buyerAccountId || userId;
      if (filter === "Monthly") {
        dispatch(
          getMonthlyTransactionData({
            supplierId: supplier._id,
            buyerId: buyerIdToUse,
          })
        );
      } else if (filter === "Last 3 Months") {
        dispatch(
          getLastThreeMonthsTransactionData({
            supplierId: supplier._id,
            buyerId: buyerIdToUse,
          })
        );
      } else if (filter === "Yearly") {
        dispatch(
          getYearlyTransactionData({
            supplierId: supplier._id,
            buyerId: buyerIdToUse,
          })
        );
      }
      setDialogOpen(false);
      setEditableRowData(null);
    } catch (error) {
      console.error("Failed to update transaction:", error);
    }
  };

  const handleDeleteRow = async (budgetId) => {
    const buyerIdToUse = user?.isTestUser
      ? user?.id
      : user?.buyerAccountId || userId;
    await dispatch(
      deleteBudget({
        supplierId: supplier._id,
        buyerId: buyerIdToUse,
        budgetIds: [budgetId],
      })
    );
    if (filter === "Monthly") {
      dispatch(
        getMonthlyTransactionData({
          supplierId: supplier._id,
          buyerId: buyerIdToUse,
        })
      );
    } else if (filter === "Last 3 Months") {
      dispatch(
        getLastThreeMonthsTransactionData({
          supplierId: supplier._id,
          buyerId: buyerIdToUse,
        })
      );
    } else if (filter === "Yearly") {
      dispatch(
        getYearlyTransactionData({
          supplierId: supplier._id,
          buyerId: buyerIdToUse,
        })
      );
    }
    setOpenMenuIndex(null);
  };

  const handleChange = (option) => {
    setSelectedOption(option);
    handleSearch(option.value);
  };

  const toggleMenu = (index) => {
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  return (
    <>
      <div className="flex flex-col mr-4 mt-8">
        <span className="flex justify-start items-start text-lg font-semibold mb-4">
          Previous Spend Data
        </span>
        <div className="flex flex-row justify-between items-center gap-6">
          <div className="flex flex-row sm:items-center sm:justify-start gap-2 sm:gap-2 w-full sm:w-auto">
            <div
              style={{
                position: "relative",
                display: "inline-block",
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
              ></svg>

              <input
                type="text"
                placeholder="Input your prompt to match your search"
                aria-label="Search"
                className="supplier-search-input pl-[35px] text-[#A8A8A8] italic font-normal w-full"
                style={{ minWidth: 0 }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <button
              onClick={() => handleSearch()}
              type="submit"
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg bg-[#08F] font-semibold border-none cursor-pointer whitespace-nowrap"
            >
              Search
            </button>
          </div>
          <div className="flex flex-row justify-center items-center gap-4">
            <Select
              options={optionsList}
              className="min-w-[120px]"
              value={selectedOption}
              onChange={handleChange}
              styles={customStyles}
            />

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="19"
              height="19"
              viewBox="0 0 19 19"
              fill="none"
              onClick={() => setDialogOpen(true)}
            >
              <path
                d="M0.185547 18.5V13.5H2.18555V16.5H5.18555V18.5H0.185547ZM13.1855 18.5V16.5H16.1855V13.5H18.1855V18.5H13.1855ZM0.185547 5.5V0.5H5.18555V2.5H2.18555V5.5H0.185547ZM16.1855 5.5V2.5H13.1855V0.5H18.1855V5.5H16.1855Z"
                fill="black"
              />
            </svg>
          </div>
        </div>
      </div>
      <div className="pb-12 overflow-x-auto w-full">
        <table className="w-full table-auto bg-white shadow rounded-lg">
          <thead className="bg-[#F7F7F7] text-sm font-medium text-[#1D2739] text-center border-b">
            <tr>
              <th className="p-4">{filter === "Yearly" ? "Year" : "Month"}</th>
              <th className="p-4">Budget</th>
              <th className="p-4">Spend</th>
              <th className="p-4">Spend status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filteredData.slice(0, 5).map((txn, index) => {
              const spendStatus = getSpendStatus(txn);
              return (
                <tr
                  key={index}
                  className="text-sm border-b hover:bg-gray-50 text-center"
                >
                  <td className="p-4">
                    {filter === "Yearly" ? txn.year : txn.month}
                  </td>
                  <td className="p-4 whitespace-nowrap">$ {txn.budget}</td>
                  <td className="p-4">$ {txn.amount}</td>
                  <td className="p-4">
                    <span
                      className={`text-xs px-2 py-1 rounded ${statusColors[spendStatus]}
                    }`}
                    >
                      {spendStatus}
                    </span>
                  </td>
                  <td className="relative p-4">
                    {filter !== "Yearly" && (
                      <>
                        <button
                          onClick={() => toggleMenu(index)}
                          className="text-gray-500 hover:text-gray-800"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M8.9579 4.1951C9.37691 4.1951 9.71658 3.85542 9.71658 3.43642C9.71658 3.01741 9.37691 2.67773 8.9579 2.67773C8.53889 2.67773 8.19922 3.01741 8.19922 3.43642C8.19922 3.85542 8.53889 4.1951 8.9579 4.1951Z"
                              stroke="#1D2739"
                              stroke-width="1.51736"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M8.9579 9.75857C9.37691 9.75857 9.71658 9.4189 9.71658 8.99989C9.71658 8.58088 9.37691 8.24121 8.9579 8.24121C8.53889 8.24121 8.19922 8.58088 8.19922 8.99989C8.19922 9.4189 8.53889 9.75857 8.9579 9.75857Z"
                              stroke="#1D2739"
                              stroke-width="1.51736"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M8.9579 15.3221C9.37691 15.3221 9.71658 14.9824 9.71658 14.5634C9.71658 14.1444 9.37691 13.8047 8.9579 13.8047C8.53889 13.8047 8.19922 14.1444 8.19922 14.5634C8.19922 14.9824 8.53889 15.3221 8.9579 15.3221Z"
                              stroke="#1D2739"
                              stroke-width="1.51736"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </button>

                        {!isDialogOpen && openMenuIndex === index && (
                          <div className="absolute right-4 top-10 z-10 bg-white border rounded shadow-md w-32">
                            <ul className="text-sm text-left">
                              <li
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => {
                                  setEditableRowData(txn);
                                  setEditDialogOpen(true);
                                  setOpenMenuIndex(null);
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <Edit2 size={14} /> Edit budget
                                </div>
                              </li>
                              <li
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleDeleteRow(txn.budgetId)}
                              >
                                <div className="flex items-center gap-2 text-red-600">
                                  <Trash2 size={14} /> Delete
                                </div>
                              </li>
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isDialogOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 ml-24"
          onClick={() => setDialogOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-h-[80vh] w-[85vw] md:w-[65vw] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-row justify-between items-center mb-4">
              <h2 className="flex justify-center items-center text-xl font-semibold ">
                {viewMode === "search"
                  ? "Search Results"
                  : "All Previous Spend Data"}
              </h2>
              <button
                onClick={() => {
                  setDialogOpen(false);
                }}
                className="px-4 py-2 bg-[#EDEDED] rounded"
              >
                Close
              </button>
            </div>
            <table className="w-full table-auto bg-white shadow rounded-lg">
              <thead className="bg-[#F7F7F7] text-sm font-medium text-[#1D2739] text-center border-b">
                <tr>
                  <th className="p-4">
                    {filter === "Yearly" ? "Year" : "Month"}
                  </th>
                  <th className="p-4">Budget</th>
                  <th className="p-4">Spend</th>
                  <th className="p-4">Spend status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((txn, index) => {
                  const spendStatus = getSpendStatus(txn);
                  return (
                    <tr
                      key={index}
                      className="text-sm border-b hover:bg-gray-50 text-center"
                    >
                      <td className="p-4">
                        {filter === "Yearly" ? txn.year : txn.month}
                      </td>
                      <td className="p-4 whitespace-nowrap">$ {txn.budget}</td>
                      <td className="p-4">$ {txn.amount}</td>
                      <td className="p-4">
                        <span
                          className={`text-xs px-2 py-1 rounded ${statusColors[spendStatus]}
                        }`}
                        >
                          {spendStatus}
                        </span>
                      </td>
                      <td className="relative p-4">
                        {filter !== "Yearly" && (
                          <>
                            <button
                              onClick={() => toggleMenu(index)}
                              className="text-gray-500 hover:text-gray-800"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                              >
                                <path
                                  d="M8.9579 4.1951C9.37691 4.1951 9.71658 3.85542 9.71658 3.43642C9.71658 3.01741 9.37691 2.67773 8.9579 2.67773C8.53889 2.67773 8.19922 3.01741 8.19922 3.43642C8.19922 3.85542 8.53889 4.1951 8.9579 4.1951Z"
                                  stroke="#1D2739"
                                  stroke-width="1.51736"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                                <path
                                  d="M8.9579 9.75857C9.37691 9.75857 9.71658 9.4189 9.71658 8.99989C9.71658 8.58088 9.37691 8.24121 8.9579 8.24121C8.53889 8.24121 8.19922 8.58088 8.19922 8.99989C8.19922 9.4189 8.53889 9.75857 8.9579 9.75857Z"
                                  stroke="#1D2739"
                                  stroke-width="1.51736"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                                <path
                                  d="M8.9579 15.3221C9.37691 15.3221 9.71658 14.9824 9.71658 14.5634C9.71658 14.1444 9.37691 13.8047 8.9579 13.8047C8.53889 13.8047 8.19922 14.1444 8.19922 14.5634C8.19922 14.9824 8.53889 15.3221 8.9579 15.3221Z"
                                  stroke="#1D2739"
                                  stroke-width="1.51736"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </button>

                            {openMenuIndex === index && (
                              <div className="absolute right-4 top-10 z-10 bg-white border rounded shadow-md w-32">
                                <ul className="text-sm text-left">
                                  <li
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => {
                                      setEditableRowData(txn);
                                      setEditDialogOpen(true);
                                      setOpenMenuIndex(null);
                                    }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Edit2 size={14} /> Edit budget
                                    </div>
                                  </li>
                                  <li
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                    onClick={() =>
                                      handleDeleteRow(txn.budgetId)
                                    }
                                  >
                                    <div className="flex items-center gap-2 text-red-600">
                                      <Trash2 size={14} /> Delete
                                    </div>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isEditDialogOpen && editableRowData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 ml-24"
          onClick={() => {
            setDialogOpen(false);
            setEditableRowData(null);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-h-[80vh] w-[85vw] md:w-[65vw] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-row justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Edit Spend Data</h2>
              <button
                onClick={() => {
                  setDialogOpen(false);
                  setEditableRowData(null);
                }}
                className="px-4 py-2 bg-[#EDEDED] rounded"
              >
                Close
              </button>
            </div>

            <table className="w-full table-auto bg-white shadow rounded-lg">
              <thead className="bg-[#F7F7F7] text-sm font-medium text-[#1D2739] text-center border-b">
                <tr>
                  <th className="p-4">
                    {filter === "Yearly" ? "Year" : "Month"}
                  </th>
                  <th className="p-4">Budget</th>
                  {/* <th className="p-4">Spend</th> */}
                </tr>
              </thead>
              <tbody>
                <tr className="text-sm border-b text-center">
                  <td className="p-4">
                    <input
                      type="text"
                      className="border rounded px-2 py-1 w-full"
                      value={
                        filter === "Yearly"
                          ? editableRowData.year
                          : editableRowData.month
                      }
                      onChange={(e) =>
                        setEditableRowData({
                          ...editableRowData,
                          ...(filter === "Yearly"
                            ? { year: e.target.value }
                            : { month: e.target.value }),
                        })
                      }
                    />
                  </td>
                  <td className="p-4">
                    <input
                      type="number"
                      className="border rounded px-2 py-1 w-full"
                      value={editableRowData.budget}
                      onChange={(e) =>
                        setEditableRowData({
                          ...editableRowData,
                          budget: e.target.value,
                        })
                      }
                    />
                  </td>
                  {/* <td className="p-4">
                    <input
                      type="number"
                      className="border rounded px-2 py-1 w-full"
                      value={editableRowData.amount}
                      onChange={(e) =>
                        setEditableRowData({
                          ...editableRowData,
                          amount: e.target.value,
                        })
                      }
                    />
                  </td> */}
                </tr>
              </tbody>
            </table>

            <div className="flex justify-end items-end mt-12 ">
              <button
                className="px-4 py-1 bg-[#EAF524] text-black rounded"
                onClick={handleUpdateBudget}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
